import { captureMessage } from '@sentry/vue';
import { useEventListener, useThrottleFn } from '@vueuse/core';
import { Messages } from '@/src/constants';
import { useMessageStore } from '@/src/store/messages';
import { View } from '@/src/core/vtk/types';
import { vtkFieldRef } from '@/src/core/vtk/vtkFieldRef';
import { MaybeRef, computed, inject, onScopeDispose, unref } from 'vue';
import { Maybe } from '@/src/types';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { baoTrangCha } from '@/src/utils/trangCha';
import { ghiNhanContext } from '@/src/utils/matWebGL';
import { VtkRenderWindowParentContext } from '@/src/components/vtk/context';

const THROTTLE_THRESHOLD = 250; // ms

/**
 * Collects relevant context for debugging 3D crashes.
 * @returns
 */
function getVolumeMapperContext(view: View) {
  const ren = view.renderer;
  const vol = ren.getVolumes()[0];
  if (!vol) return null;

  const mapper = vol.getMapper();
  if (!mapper) return null;

  return mapper.get(
    'computeNormalFromOpacity',
    'autoAdjustSampleDistances',
    'maximumSamplesPerRay',
    'sampleDistance',
    'volumetricScatteringBlending'
  );
}

// Các khung dùng chung canvas WebGL gốc: một lần mất context chỉ báo một lần, dù mọi khung cùng nghe.
const daBao = new WeakSet<EventTarget>();

export function useWebGLWatchdog(view: MaybeRef<Maybe<View>>) {
  const { currentImageData } = useCurrentImage();
  const reportError = useThrottleFn(() => {
    const messageStore = useMessageStore();
    messageStore.addError(Messages.WebGLLost.title, {
      details: Messages.WebGLLost.details,
    });

    const khoi = currentImageData.value?.getDimensions();
    baoTrangCha('viewer-gpu-lost', {
      viewer: 'volview',
      ...(khoi ? { khoi: Array.from(khoi) } : {}),
    });

    const contexts: Record<string, any> = {};
    const viewVal = unref(view);
    if (viewVal) {
      contexts.vtk = getVolumeMapperContext(viewVal);
    }

    captureMessage('WebGL2 context was lost', { contexts });
  }, THROTTLE_THRESHOLD);

  // Trong VtkRenderWindowParent canvas của từng khung chỉ là 2D chép ảnh từ render window gốc; WebGL (và sự kiện mất
  // context) nằm ở canvas gốc.
  const parent = inject(VtkRenderWindowParentContext, null);
  const renWinView = computed(
    () => parent?.renderWindowView ?? unref(view)?.renderWindowView
  );
  const canvas = vtkFieldRef(renWinView, 'canvas');
  // getContext() của vtk.js chỉ đọc context đã có (bản .d.ts không khai báo), không tạo context mới.
  const renWin = () =>
    renWinView.value as Maybe<{ getContext?: () => WebGL2RenderingContext }>;
  onScopeDispose(ghiNhanContext(() => renWin()?.getContext?.()));
  useEventListener(canvas, 'webglcontextlost', (e: Event) => {
    if (!e.target || daBao.has(e.target)) return;
    daBao.add(e.target);
    reportError();
  });
}
